/**
 * Friction capture for the telemetry `friction` group: clicks that show the user is stuck.
 *
 * - `friction.rage_click`: a burst of clicks on one spot (not in a text field, not a
 *   text selection), one event per burst after it ends.
 * - `friction.dead_click`: a click on something that looks clickable followed by
 *   nothing — no DOM mutation outside a `[data-telemetry-ticking]` subtree (regions that
 *   change on their own), no scroll, navigation, request, error or focus loss. Requests
 *   and console errors arrive over lib/telemetry/activity.js. A readout (`role="img"` with a
 *   `data-ui`, shown on hover or focus) is not clickable.
 * - `friction.error_click`: a console or uncaught error soon after a click; never its
 *   text (the diagnostics group's `console.error` carries that). A React development
 *   warning (only the integration debug build logs them) is not announced by
 *   services/consoleCapture.js, so it never counts; uncaught errors always do.
 *
 * Events carry `action.click`'s target shape, `ui_key` and the nearest `data-surface`.
 */
import { onActivity } from "../lib/telemetry/activity.js";
import {
  clickedElement,
  describeTarget,
  elementOf,
  isStateControl,
  isTextField,
  nearestInstrument,
} from "./interactionCapture.js";

export const RAGE_MIN_CLICKS = 3;
export const RAGE_WINDOW_MS = 1000;
export const RAGE_RADIUS_PX = 30;
export const DEAD_WAIT_MS = 1000;
export const ERROR_WINDOW_MS = 1000;
/** Marks a subtree that changes by itself (progress, animation, a streaming reply). */
export const TICKING_ATTR = "data-telemetry-ticking";

/** What a dead click must land on: something the user expects to act when clicked. */
const CLICKABLE =
  'button, a[href], summary, [role="button"], [role="link"], [role="tab"], [role="menuitem"], [role="option"], [role="switch"], [role="treeitem"], [data-ui], [onclick]';

/**
 * A readout: a `data-ui` element with `role="img"` (a chart point) whose action is showing its
 * numbers on hover or focus. A click on it is not pressing anything, so it is never dead.
 */
const isReadout = (el) => el.getAttribute?.("role") === "img" && !!el.hasAttribute?.("data-ui");

/** A friction target: an id, optionally `[key]`; no `:` outside the key (aria-label targets). */
const FRICTION_TARGET_RE = /^[A-Za-z0-9_.#/-]{1,128}(?:\[[A-Za-z0-9_.:#/-]{1,128}\])?$/;

/**
 * The friction fields for a click on `el`: target, tag, role, part, `ui_key`, surface. Ids
 * only: never `action.click`'s accessible name or region path, and no aria-label target
 * (`label:…`, `toolbar:…`), which carries user-facing text.
 */
export function frictionTarget(el) {
  const { target, tag, role, part } = describeTarget(clickedElement(el));
  const out = { target: FRICTION_TARGET_RE.test(target ?? "") ? target : null, tag };
  if (role) out.role = role;
  if (part) out.part = part;
  const ui = nearestInstrument(el, "data-ui");
  if (ui?.key) out.ui_key = ui.key;
  const surface = nearestInstrument(el, "data-surface");
  if (surface) {
    out.surface = surface.name;
    if (surface.key) out.surface_key = surface.key;
  }
  return out;
}

/** Create the capture; `record(event)` receives each `friction.*` event. */
export function createFrictionCapture({
  record,
  doc = typeof document !== "undefined" ? document : null,
  win = typeof window !== "undefined" ? window : null,
  now = () => (typeof performance !== "undefined" ? performance.now() : Date.now()),
  setTimeoutImpl = (fn, ms) => setTimeout(fn, ms),
  clearTimeoutImpl = (id) => clearTimeout(id),
  MutationObserverImpl = typeof MutationObserver !== "undefined" ? MutationObserver : null,
  subscribe = onActivity,
  // Synthetic clicks (`el.click()`, dispatchEvent) are the app's, not the user's.
  acceptUntrusted = false,
} = {}) {
  let started = false;
  let listeners = [];
  let unsubscribe = null;
  /** Recent clicks not yet in a burst: `{t, x, y}`. */
  let recent = [];
  /** The rage burst being counted: `{x, y, first, last, count, el, target, timer}`. */
  let burst = null;
  /** The click waiting to be judged dead: `{el, t, href, timer}`. */
  let pending = null;
  let observer = null;
  /** The last click, for error clicks: `{el, t, reported}`. */
  let lastClick = null;

  function emit(event) {
    try {
      record(event);
    } catch {
      /* never surface */
    }
  }

  function listen(target, type, fn, capture = true) {
    if (!target) return;
    target.addEventListener(type, fn, { capture, passive: true });
    listeners.push([target, type, fn, capture]);
  }

  const looksClickable = (el) => {
    const hit = el.closest?.(CLICKABLE);
    if (hit) return !isReadout(hit);
    try {
      return win?.getComputedStyle?.(el)?.cursor === "pointer";
    } catch {
      return false;
    }
  };

  /** Whether the user has text selected in or around `el` (a multi-click selects). */
  function selectsText(el) {
    try {
      const sel = win?.getSelection?.();
      if (!sel || sel.isCollapsed || String(sel).length === 0) return false;
      return typeof sel.containsNode === "function" ? sel.containsNode(el, true) : true;
    } catch {
      return false;
    }
  }

  // --- rage -----------------------------------------------------------------

  function flushRage() {
    const b = burst;
    burst = null;
    if (!b) return;
    if (b.timer !== null) clearTimeoutImpl(b.timer);
    // Triple-clicking text selects it: that is reading, not rage.
    if (selectsText(b.el)) return;
    emit({
      event: "friction.rage_click",
      ...b.target,
      clicks: b.count,
      window_ms: Math.round(b.last - b.first),
    });
  }

  function armRage() {
    if (burst.timer !== null) clearTimeoutImpl(burst.timer);
    burst.timer = setTimeoutImpl(flushRage, RAGE_WINDOW_MS);
  }

  function trackRage(el, target, t, x, y) {
    if (isTextField(el)) return;
    if (burst) {
      if (
        t - burst.last <= RAGE_WINDOW_MS &&
        Math.hypot(burst.x - x, burst.y - y) <= RAGE_RADIUS_PX
      ) {
        burst.count += 1;
        burst.last = t;
        burst.el = el;
        burst.target = target;
        armRage();
        return;
      }
      flushRage();
    }
    recent = recent.filter(
      (c) => t - c.t <= RAGE_WINDOW_MS && Math.hypot(c.x - x, c.y - y) <= RAGE_RADIUS_PX,
    );
    recent.push({ t, x, y });
    if (recent.length < RAGE_MIN_CLICKS) return;
    const first = recent[0];
    burst = {
      x: first.x,
      y: first.y,
      first: first.t,
      last: t,
      count: recent.length,
      el,
      target,
      timer: null,
    };
    recent = [];
    armRage();
  }

  // --- dead -----------------------------------------------------------------

  function settleDead() {
    const p = pending;
    pending = null;
    observer?.disconnect();
    if (p && p.timer !== null) clearTimeoutImpl(p.timer);
    return p;
  }
  /** Something happened: the pending click was not dead. */
  function activity() {
    if (pending) settleDead();
  }

  /** A childList record that only removes ticking regions. */
  function endsTicking(r) {
    if ((r.addedNodes?.length ?? 0) > 0) return false;
    const removed = [...(r.removedNodes ?? [])];
    return (
      removed.length > 0 && removed.every((n) => n.nodeType === 1 && n.hasAttribute?.(TICKING_ATTR))
    );
  }

  function onMutations(records) {
    if (!pending) return;
    // Records are read after the fact: a region that stopped ticking in this batch has
    // already lost its attribute, so its own records still count as ticking. A region
    // that starts ticking (a click put it into a loading state) is the click's effect.
    const isTicking = (r) => r.type === "attributes" && r.attributeName === TICKING_ATTR;
    const stopped = [];
    for (const r of records) {
      if (!isTicking(r)) continue;
      if (r.oldValue === null || r.oldValue === undefined) {
        activity();
        return;
      }
      if (!r.target.hasAttribute?.(TICKING_ATTR)) stopped.push(r.target);
    }
    for (const r of records) {
      if (isTicking(r)) continue;
      // A ticking region unmounting (its work finished) is not the click's effect; the
      // record's target is the surviving parent, so the marker is on the removed nodes.
      if (r.type === "childList" && endsTicking(r)) continue;
      const node = r.target;
      const el = node && node.nodeType === 1 ? node : node?.parentElement;
      if (!el || el.closest?.(`[${TICKING_ATTR}]`)) continue;
      if (stopped.some((root) => root === el || root.contains?.(el))) continue;
      activity();
      return;
    }
  }

  function judgeDead() {
    const p = settleDead();
    if (!p) return;
    if ((win?.location?.href ?? null) !== p.href) return;
    emit({
      event: "friction.dead_click",
      ...p.target,
      wait_ms: Math.round(now() - p.t),
    });
  }

  function trackDead(e, el, target, t) {
    if (e.button !== undefined && e.button !== 0) return;
    // Ctrl/Meta/Shift-click open elsewhere; nothing on this page is expected to change.
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
    const subject = clickedElement(el);
    const tag = (subject.tagName || "").toLowerCase();
    // Typing into a field, toggling a box (also through its label) and downloads change
    // nothing in the DOM by design.
    if (isTextField(subject) || isStateControl(subject) || tag === "label") return;
    if (tag === "a" && subject.hasAttribute?.("download")) return;
    // Checked now, not when judged: a blank-area click never costs an observer.
    if (!looksClickable(subject)) return;
    if (pending) {
      if (pending.el === subject) return;
      settleDead();
    }
    const body = doc?.body;
    if (!MutationObserverImpl || !body) return;
    try {
      observer ||= new MutationObserverImpl(onMutations);
      observer.observe(body, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeOldValue: true,
        characterData: true,
      });
    } catch {
      return;
    }
    pending = { el: subject, target, t, href: win?.location?.href ?? null, timer: null };
    pending.timer = setTimeoutImpl(judgeDead, DEAD_WAIT_MS);
  }

  // --- error ----------------------------------------------------------------

  function onError() {
    const c = lastClick;
    if (!c || c.reported) return;
    const delay = now() - c.t;
    if (delay > ERROR_WINDOW_MS) return;
    c.reported = true;
    activity(); // an error click is not also a dead one
    emit({
      event: "friction.error_click",
      ...c.target,
      delay_ms: Math.round(delay),
    });
  }

  // --- listeners ------------------------------------------------------------

  function onClick(e) {
    if (!acceptUntrusted && e.isTrusted === false) return;
    const el = elementOf(e.target);
    if (!el) return;
    const t = now();
    // Described at click time: by the time a burst ends or an error arrives, the node
    // may be relabelled, moved or gone.
    const target = frictionTarget(el);
    // detail 0 is a keyboard activation: no position, so no rage burst.
    if (e.detail !== 0 && Number.isFinite(e.clientX) && Number.isFinite(e.clientY)) {
      trackRage(el, target, t, e.clientX, e.clientY);
    }
    lastClick = { target, t, reported: false };
    trackDead(e, el, target, t);
  }

  function onWindowError(e) {
    // A failed image or script load fires `error` on its element, not the window.
    if (e && e.target && e.target !== win) return;
    onError();
  }

  function onBus(kind) {
    if (kind === "error") onError();
    else activity();
  }

  const onVisibility = () => {
    if (doc?.visibilityState === "hidden") activity();
  };

  return {
    /** Install the listeners; idempotent. */
    start() {
      if (started || !doc) return;
      started = true;
      listen(doc, "click", onClick);
      listen(doc, "scroll", activity);
      listen(doc, "visibilitychange", onVisibility);
      listen(win, "popstate", activity, false);
      listen(win, "hashchange", activity, false);
      // A new tab, a file picker or a download dialog takes the focus away.
      listen(win, "blur", activity, false);
      listen(win, "pagehide", activity, false);
      listen(win, "error", onWindowError, false);
      listen(win, "unhandledrejection", onError, false);
      unsubscribe = subscribe(onBus);
    },
    /** Remove the listeners and drop pending clicks without reporting them; idempotent. */
    stop() {
      if (!started) return;
      started = false;
      for (const [target, type, fn, capture] of listeners) {
        target.removeEventListener(type, fn, { capture });
      }
      listeners = [];
      unsubscribe?.();
      unsubscribe = null;
      if (burst?.timer != null) clearTimeoutImpl(burst.timer);
      burst = null;
      recent = [];
      settleDead();
      observer = null;
      lastClick = null;
    },
    get started() {
      return started;
    },
  };
}
